import styled from '@emotion/styled';
import { Typography } from '@material-ui/core';
import { createPath } from 'history';
import { parse } from 'qs';
import { createElement, Fragment, ReactElement, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect } from 'react-router';

import State from '../../../core/State';
import { OrganizationDetailFragment } from '../../../generated/graphql';
import theme from '../../../theme';
import mergeUrlQuery from '../../../utility/mergeUrlQuery';
import DraftProjectDeleteDialog from '../../component/compound/DraftProjectDeleteDialog';
import LatestInsightsList from '../../component/compound/LatestInsightsList';
import ProjectsList, { ProjectListItem } from '../../component/compound/ProjectsList';
import TransitionToKaizenIdModal from '../../component/compound/TransitionToKaizenIdModal';
import { SCREEN_LARGE } from '../../constant/pageWidth';
import { SPACING_HUGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { context as ConfigContext } from '../../context/config';
import { context as dependencyContext } from '../../context/dependency';
import { context as historyContext } from '../../context/history';
import { context as stateContext } from '../../context/state';
import mq from '../../styleUtility/mediaquery';
import useGetInsightCategories from './hooks';
import ProjectsNavButton from './ProjectsNavButton';

const HomePage = () => {
  const dependency = useContext(dependencyContext);
  const state = useContext(stateContext);
  const history = useContext(historyContext);
  const config = useContext(ConfigContext);

  const [isModalOpen, setModal] = useState(false);
  const [projectToDelete, setDeleteDialog] = useState<{ id: string } | null>(null);

  const query = parse(state.location.search.slice(1));
  const organizationId: string | undefined = query.org;

  const user = state.authentication.signedInUser;
  const language = state.language;

  const projectsLoading = state.latestProjects.fetching >= 1;
  const projects = state.latestProjects.items;
  const hasDraftItems = state.latestProjects.hasDraftItems;

  const { insightCategories, loading } = useGetInsightCategories({ language, organizationId });

  const projectOrderTarget = 'updatedAt';
  const showEmptyProject = useMemo(() => !projectsLoading && projects.length === 0, [projects.length, projectsLoading]);
  const showEmptyInsight = useMemo(() => !loading && insightCategories?.length === 0, [
    insightCategories?.length,
    loading,
  ]);

  useEffect(() => {
    dependency.authenticationUsecase.needSignedIn();
    if (organizationId) dependency.listInsightCategoryUsecase.prepareProjects(organizationId);

    return () => {
      dependency.listInsightCategoryUsecase.dispose();
    };
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  const projectsItem = projects.map((project) => (
    <ProjectListItem
      project={project}
      onClickDeleteButton={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDeleteDialog(project);
      }}
      hasDraftProject={hasDraftItems.some((hasDraft) => hasDraft === project.id)}
      key={project.id}
      projectOrderTarget={projectOrderTarget}
    />
  ));

  const getOrganization = ({
    lastAccessedOrganization,
    availableOrganizations,
  }: {
    lastAccessedOrganization: State['lastAccessedOrganization'];
    availableOrganizations: State['availableOrganizations'];
  }): OrganizationDetailFragment | undefined => {
    if (lastAccessedOrganization) {
      return lastAccessedOrganization;
    } else if (availableOrganizations.items.length >= 1) {
      return availableOrganizations.items[0];
    } else {
      return undefined;
    }
  };

  const organization = getOrganization(state);

  if (!organizationId && organization) {
    return <Redirect to={createPath(mergeUrlQuery(history.location, { org: organization.id }))} />;
  } else if (organizationId != null && !state.availableOrganizations.items.some((item) => item.id === organizationId)) {
    return <Redirect to="/latest-insights" />;
  } else {
    return (
      <Fragment>
        <Root>
          <Heading>
            <Title>
              <FormattedMessage id="latest_insights_page.latest_projects" />
            </Title>
          </Heading>

          <ProjectsList showEmptyState={showEmptyProject} loading={projects.length === 0 && projectsLoading} isLatest>
            {projectsItem as Array<ReactElement>}
          </ProjectsList>

          <ProjectsNavButton
            projectsLoading={projectsLoading}
            showEmptyProject={showEmptyProject}
            redirectToDirectionTypes={() => history.push(`/direction_types`)}
            organization={state.lastAccessedOrganization}
            user={user}
            language={language}
            setModal={setModal}
            onGoToProjectClick={() => dependency.listInsightCategoryUsecase.goToProjects(organizationId)}
          />

          <LatestInsightsList
            loading={loading}
            showEmptyState={showEmptyInsight}
            insightCategories={insightCategories}
            isSingleCategoryPage={false}
          />
        </Root>

        <DraftProjectDeleteDialog
          onClickAcceptButton={async () => {
            setDeleteDialog(null);
            await dependency.deleteProjectUsecase.deleteDraftProject(projectToDelete!).then(() => {
              if (organizationId) {
                dependency.listInsightCategoryUsecase.prepareProjects(organizationId);
              }
            });
          }}
          onRequestClose={() => setDeleteDialog(null)}
          open={!!projectToDelete}
        />

        {state.authentication.signedInUser ? (
          <TransitionToKaizenIdModal
            urlToId={config.ID_WEB_URL}
            open={isModalOpen}
            language={state.language}
            onRequestClose={() => setModal(false)}
          />
        ) : null}
      </Fragment>
    );
  }
};

const Root = styled('div')`
  width: auto;
  max-width: ${SCREEN_LARGE}px;
  margin-top: ${SPACING_HUGE}px;
  margin-right: auto;
  margin-left: auto;
  padding-bottom: ${SPACING_UNIT * 12}px;
  ${mq('sp')} {
    padding-left: ${SPACING_REGULAR}px;
    padding-right: ${SPACING_REGULAR}px;
  }
`;

const Heading = styled('div')`
  margin-bottom: ${SPACING_UNIT * 3}px;
  ${mq('sp')} {
    margin-bottom: ${SPACING_UNIT}px;
  }
`;

const Title = styled(Typography)`
  font-size: ${theme.typography.h5.fontSize};
`;

export default HomePage;
